import { useState, useEffect, useMemo } from "react";
import FilterSelect, { type FilterOption } from '@/shared/components/FilterSelect';
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Clock,
  Percent,
  FileText,
  Eye,
  Trash2,
  CheckCheck,
  MoreVertical,
  MessageSquare,
  XCircle,
} from "lucide-react";
import { apiService } from '@/services/api';
import { formatDate } from '@/shared/lib/dateUtils';
import ConfirmActionModal from '@/shared/components/ConfirmActionModal';
import Toast from '@/shared/components/Toast';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import KpiCard from "@/features/rh/components/dashboard/KpiCard";
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import { Badge } from "@/shared/components/Badge";

interface SupplierGeneral {
  legalName: string;
  commercialName: string | null;
  rfc: string;
  salesEmail: string;
  accountsPayableEmail: string;
  mainContactName: string;
  mainContactPhone: string;
  internalRequesterName: string;
  repseNumber: string | null;
  personType: string;
  vatType: string;
  serviceTypeId: string;
  country: string;
  state: string;
  city: string;
  municipality: string;
  suburb: string;
  postalCode: string;
  street: string;
  externalNumber: string;
  internalNumber: string | null;
}

interface Supplier {
  id: string;
  rfc: string;
  legalName: string;
  commercialName: string | null;
  personType: string;
  vatType: string;
  serviceTypeId: string;
  country: string;
  state: string;
  city: string;
  municipality: string;
  suburb: string;
  postalCode: string;
  street: string;
  externalNumber: string;
  internalNumber: string | null;
  mainContactName: string;
  mainContactPhone: string;
  salesEmail: string;
  accountsPayableEmail: string;
  internalRequesterName: string;
  repseNumber: string | null;
  taxRegime: string | null;
  cfdiUse: string | null;
  taxEmail: string | null;
  taxPhone: string | null;
  origin: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  hasAllDocumentsApproved: boolean;
  hasAllRequiredDocsUploaded?: boolean;
  calculatedStatus?: string;
  general?: SupplierGeneral;
}

interface SuppliersResponse {
  data: Supplier[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const SolicitudesProveedores = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProveedor, setFilterProveedor] = useState("");
  const [filterEstatus, setFilterEstatus] = useState("");
  const [filterDesde, setFilterDesde] = useState("");
  const [filterHasta, setFilterHasta] = useState("");
  const [solicitudes, setSolicitudes] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionSupplierId, setActionSupplierId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const fetchSolicitudes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.request<SuppliersResponse>(
        "/internal/suppliers?status=sent_to_review,pending_purchases,pending_controlling,pending_info,rejected,draft",
      );
      setSolicitudes(response.data || []);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar solicitudes:", err);
      setError(err.message || "Error al cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitudes();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openMenuId &&
        !(event.target as Element).closest(
          ".action-menu-container, .action-menu-portal",
        )
      ) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  const toggleMenu = (e: React.MouseEvent, supplierId: string) => {
    e.stopPropagation();

    if (openMenuId === supplierId) {
      setOpenMenuId(null);
      setMenuPosition(null);
      return;
    }

    const button = e.currentTarget as HTMLButtonElement;
    const rect = button.getBoundingClientRect();
    const menuWidth = 160; // w-40
    const menuHeight = 260;
    const viewportPadding = 12;

    const top = Math.min(
      rect.bottom + 4,
      window.innerHeight - menuHeight - viewportPadding,
    );
    const left = Math.max(
      viewportPadding,
      Math.min(
        rect.right - menuWidth,
        window.innerWidth - menuWidth - viewportPadding,
      ),
    );

    setMenuPosition({ top, left });
    setOpenMenuId(supplierId);
  };

  const handleApprove = (supplierId: string) => {
    setActionSupplierId(supplierId);
    setShowApproveModal(true);
  };

  const handleReject = (supplierId: string) => {
    setActionSupplierId(supplierId);
    setShowRejectModal(true);
  };

  const handleObserve = (supplierId: string) => {
    navigate(`/solicitudes-proveedores/${supplierId}/editar`);
  };

  const handleDelete = (supplierId: string) => {
    setActionSupplierId(supplierId);
    setShowDeleteModal(true);
  };

  const canApproveSupplier = (supplier: Supplier) => {
    return supplier.hasAllDocumentsApproved === true;
  };

  const confirmApprove = async () => {
    if (!actionSupplierId) return;

    try {
      setActionLoading(true);
      await apiService.request(
        `/internal/suppliers/${actionSupplierId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status: "approved" }),
        },
      );
      setToast({ message: "Proveedor aprobado exitosamente", type: "success" });
      setShowApproveModal(false);
      setActionSupplierId(null);
      await fetchSolicitudes(); // Refrescar lista
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setToast({
        message: err.message || "Error al aprobar el proveedor",
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const confirmReject = async () => {
    if (!actionSupplierId) return;

    try {
      setActionLoading(true);
      await apiService.request(
        `/internal/suppliers/${actionSupplierId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status: "rejected" }),
        },
      );
      setToast({
        message: "Proveedor rechazado exitosamente",
        type: "success",
      });
      setShowRejectModal(false);
      setActionSupplierId(null);
      await fetchSolicitudes(); // Refrescar lista
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setToast({
        message: err.message || "Error al rechazar el proveedor",
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!actionSupplierId) return;

    try {
      setActionLoading(true);
      await apiService.request(`/admin/suppliers/${actionSupplierId}`, {
        method: "DELETE",
      });
      setToast({
        message: "Solicitud eliminada exitosamente",
        type: "success",
      });
      setShowDeleteModal(false);
      setActionSupplierId(null);
      await fetchSolicitudes();
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setToast({
        message: err.message || "Error al eliminar la solicitud",
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getSupplierName = (s: Supplier) =>
    s.legalName ||
    s.general?.legalName ||
    s.mainContactName ||
    s.general?.mainContactName ||
    "—";

  const getSupplierEmail = (s: Supplier) =>
    s.salesEmail || s.general?.salesEmail || "";

  // Etiqueta del estatus: la misma que se pinta en la tabla, para que el filtro
  // hable el idioma del usuario y no el del back.
  const estatusLabel = (solicitud: Supplier): string => {
    switch (solicitud.status) {
      case "draft":               return "Borrador";
      case "pending_info":        return "Pendiente información";
      case "rejected":            return "Rechazado";
      case "pending_purchases":   return "Pendiente compras";
      case "pending_controlling": return "Pendiente contraloría";
      case "sent_to_review":      return "En revisión";
      default:
        return solicitud.hasAllRequiredDocsUploaded
          ? "En revisión"
          : "Pendiente documentos";
    }
  };

  const proveedorOptions: FilterOption[] = useMemo(
    () =>
      [...new Set(solicitudes.map(getSupplierName).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((n) => ({ value: n, label: n })),
    [solicitudes],
  );

  const estatusOptions: FilterOption[] = useMemo(
    () =>
      [...new Set(solicitudes.map(estatusLabel))]
        .sort((a, b) => a.localeCompare(b, "es"))
        .map((e) => ({ value: e, label: e })),
    [solicitudes],
  );

  const filteredSolicitudes = solicitudes.filter((solicitud) => {
    const name = getSupplierName(solicitud).toLowerCase();
    const email = getSupplierEmail(solicitud).toLowerCase();
    const rfc = (solicitud.rfc || solicitud.general?.rfc || "").toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchTexto =
      name.includes(term) || email.includes(term) || rfc.includes(term);

    const matchProveedor =
      !filterProveedor || getSupplierName(solicitud) === filterProveedor;
    const matchEstatus = !filterEstatus || estatusLabel(solicitud) === filterEstatus;

    // Rango por fecha de creación. Se compara en YYYY-MM-DD, que es lo que
    // devuelve el input date, para no pelearse con zonas horarias.
    const creado = (solicitud.createdAt ?? "").slice(0, 10);
    const matchDesde = !filterDesde || (creado && creado >= filterDesde);
    const matchHasta = !filterHasta || (creado && creado <= filterHasta);

    return matchTexto && matchProveedor && matchEstatus && matchDesde && matchHasta;
  });

  const hayFiltros =
    !!searchTerm || !!filterProveedor || !!filterEstatus || !!filterDesde || !!filterHasta;

  const limpiarFiltros = () => {
    setSearchTerm("");
    setFilterProveedor("");
    setFilterEstatus("");
    setFilterDesde("");
    setFilterHasta("");
  };

  // KPI metrics based on the same dataset that feeds the table.
  const totalSolicitudes = solicitudes.length;

  // Solo cuentan las solicitudes que ya tienen dictamen: ahi updatedAt es el
  // momento en que se aprobo o se rechazo. Antes entraban todas, incluidas las
  // que siguen en revision, asi que el promedio se movia solo con cualquier
  // edicion aunque nadie dictaminara nada.
  const minutosHastaDictamen = solicitudes
    .filter((solicitud) => ["approved", "rejected"].includes(solicitud.status))
    .map((solicitud) => {
      const creada = new Date(solicitud.createdAt).getTime();
      const dictamen = new Date(solicitud.updatedAt).getTime();
      return (dictamen - creada) / (1000 * 60);
    })
    // Fechas invalidas o invertidas quedan fuera: antes se contaban como cero y
    // jalaban el promedio hacia abajo en vez de excluirse.
    .filter((minutos) => Number.isFinite(minutos) && minutos >= 0);

  const avgMinutes =
    minutosHastaDictamen.length > 0
      ? Math.round(
          minutosHastaDictamen.reduce((acc, minutos) => acc + minutos, 0) /
            minutosHastaDictamen.length,
        )
      : null;

  // "Primera revisión aprobada": inferred from statuses that indicate approved review.
  const approvedFirstReviewCount = solicitudes.filter((solicitud) =>
    ["pending_purchases", "pending_controlling", "approved"].includes(
      solicitud.status,
    ),
  ).length;

  const approvedFirstReviewPercent =
    totalSolicitudes > 0
      ? Math.round((approvedFirstReviewCount / totalSolicitudes) * 100)
      : 0;

  // "Observaciones o rechazos internos": rejected or sent back for additional info.
  const observationsOrRejectionsCount = solicitudes.filter((solicitud) =>
    ["rejected", "pending_info"].includes(solicitud.status),
  ).length;

  if (
    userHasPermission(
      "proveedores.administracion.proveedores.consultar",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Solicitudes para alta de proveedores
          </h1>
          <p className="text-gray-600">
            Administra las solicitudes de alta de proveedores y sus documentos
          </p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <KpiCard
            title="Tiempo promedio entre validación automática y dictamen"
            value={avgMinutes === null ? "--" : avgMinutes}
            suffix=" minutos"
            icon={<Clock />}
            variant="secondary"
            isLoading={loading}
          />
          <KpiCard
            title="Proveedores aprobados en primera revisión"
            value={approvedFirstReviewPercent}
            suffix="%"
            icon={<Percent />}
            variant="success"
            isLoading={loading}
          />
          <KpiCard
            title="Observaciones o rechazos internos"
            value={observationsOrRejectionsCount}
            icon={<FileText />}
            variant="info"
            isLoading={loading}
          />
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
          <div className="relative w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por proveedor, RFC o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
            />
          </div>

          <FilterSelect
            label="Proveedor"
            allLabel="Todos los proveedores"
            options={proveedorOptions}
            value={filterProveedor}
            onChange={setFilterProveedor}
          />

          <FilterSelect
            label="Estatus"
            allLabel="Todos los estatus"
            options={estatusOptions}
            value={filterEstatus}
            onChange={setFilterEstatus}
          />

          {/* Rango por fecha de creación */}
          <div className="relative">
            <input
              type="date"
              value={filterDesde}
              max={filterHasta || undefined}
              onChange={(e) => setFilterDesde(e.target.value)}
              title="Creadas desde"
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
              style={{ colorScheme: "light" }}
            />
          </div>
          <div className="relative">
            <input
              type="date"
              value={filterHasta}
              min={filterDesde || undefined}
              onChange={(e) => setFilterHasta(e.target.value)}
              title="Creadas hasta"
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
              style={{ colorScheme: "light" }}
            />
          </div>

          {hayFiltros && (
            <button
              type="button"
              onClick={limpiarFiltros}
              className="text-sm font-semibold text-[#315FB9] hover:underline ml-auto"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
            <EsqueletoTabla filas={7} columnas={6} />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              <strong>Error:</strong> {error}
            </p>
            <button
              onClick={fetchSolicitudes}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB] mb-[120px]">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#E5E7EB]">
                <thead className="bg-[#FAF9FF]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[40%]">
                      Proveedor
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[20%]">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[13%]">
                      Creado
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[18%]">
                      Estatus
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider w-[9%]">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E1E1E1]">
                  {filteredSolicitudes.map((solicitud) => (
                    <tr
                      key={solicitud.id}
                      className="hover:bg-[#F4F7FB] transition-colors"
                    >
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() =>
                          navigate(`/solicitudes-proveedores/${solicitud.id}`)
                        }
                      >
                        <span className="text-sm text-gray-800">
                          {getSupplierName(solicitud)}
                        </span>
                      </td>
                      <td
                        className="px-6 py-4 text-sm text-gray-500 cursor-pointer"
                        onClick={() =>
                          navigate(`/solicitudes-proveedores/${solicitud.id}`)
                        }
                      >
                        {getSupplierEmail(solicitud)}
                      </td>
                      <td
                        className="px-6 py-4 text-sm text-gray-500 cursor-pointer"
                        onClick={() =>
                          navigate(`/solicitudes-proveedores/${solicitud.id}`)
                        }
                      >
                        {formatDate(solicitud.createdAt)}
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() =>
                          navigate(`/solicitudes-proveedores/${solicitud.id}`)
                        }
                      >
                        {(() => {
                          switch (solicitud.status) {
                            case "draft":
                              return (
                                <Badge color="gris">Borrador</Badge>
                              );
                            case "pending_info":
                              return (
                                <Badge color="amarillo">Pendiente información</Badge>
                              );
                            case "rejected":
                              return (
                                <Badge color="rojo">Rechazado</Badge>
                              );
                            case "pending_purchases":
                              return (
                                <Badge color="amarillo">Pendiente compras</Badge>
                              );
                            case "pending_controlling":
                              return (
                                <Badge color="amarillo">Pendiente contraloría</Badge>
                              );
                            case "sent_to_review":
                              return (
                                <Badge color="azul">En revisión</Badge>
                              );
                            default:
                              return solicitud.hasAllRequiredDocsUploaded ? (
                                <Badge color="azul">En revisión</Badge>
                              ) : (
                                <Badge color="amarillo">Pendiente documentos</Badge>
                              );
                          }
                        })()}
                      </td>
                      <td className="px-6 py-4 relative action-menu-container">
                        {(() => {
                          const approveEnabled = canApproveSupplier(solicitud);
                          return (
                            <div className="flex justify-center">
                              <button
                                onClick={(e) => toggleMenu(e, solicitud.id)}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>

                              {openMenuId === solicitud.id &&
                                menuPosition &&
                                createPortal(
                                  <>
                                    <div
                                      className="fixed inset-0 z-10"
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        setMenuPosition(null);
                                      }}
                                    />
                                    <div
                                      className="action-menu-portal fixed w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20"
                                      style={{
                                        top: `${menuPosition.top}px`,
                                        left: `${menuPosition.left}px`,
                                      }}
                                    >
                                      <button
                                        onClick={() => {
                                          navigate(
                                            `/solicitudes-proveedores/${solicitud.id}`,
                                          );
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <Eye className="w-4 h-4" />
                                        Ver
                                      </button>
                                      <button
                                        onClick={() => {
                                          navigate(
                                            `/solicitudes-proveedores/${solicitud.id}/editar`,
                                          );
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <FileText className="w-4 h-4" />
                                        Editar
                                      </button>
                                      <button
                                        onClick={() => {
                                          if (!approveEnabled) {
                                            setToast({
                                              message:
                                                "No puedes aprobar hasta dictaminar y aprobar todos los documentos obligatorios.",
                                              type: "error",
                                            });
                                            setOpenMenuId(null);
                                            return;
                                          }
                                          handleApprove(solicitud.id);
                                          setOpenMenuId(null);
                                        }}
                                        className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${approveEnabled ? "text-gray-700 hover:bg-gray-50" : "text-gray-400 bg-gray-50 cursor-not-allowed"}`}
                                      >
                                        <CheckCheck className="w-4 h-4" />
                                        Aprobar
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleObserve(solicitud.id);
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <MessageSquare className="w-4 h-4" />
                                        Observar
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleReject(solicitud.id);
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <XCircle className="w-4 h-4" />
                                        Rechazar
                                      </button>
                                      <div className="my-1 border-t border-gray-100" />
                                      <button
                                        onClick={() => {
                                          handleDelete(solicitud.id);
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                        Eliminar
                                      </button>
                                    </div>
                                  </>,
                                  document.body,
                                )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredSolicitudes.length === 0 && (
              <div className="text-center py-12">
                {/* Con filtros puestos el vacío es otra cosa: ahí se ofrece
                    limpiarlos en vez de dar a entender que no hay solicitudes. */}
                {hayFiltros ? (
                  <>
                    <p className="text-gray-500">
                      Ninguna solicitud coincide con los filtros.
                    </p>
                    <button
                      type="button"
                      onClick={limpiarFiltros}
                      className="mt-1 text-sm font-medium text-[#315FB9] hover:underline"
                    >
                      Limpiar filtros
                    </button>
                  </>
                ) : (
                  <p className="text-gray-500">No se encontraron solicitudes</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        <ConfirmActionModal
          isOpen={showApproveModal}
          title="Aprobar Proveedor"
          message="¿Estás seguro de que deseas aprobar este proveedor? Esta acción cambiará su estado a 'Aprobado'."
          confirmText="Aprobar"
          variante="exito"
          onConfirm={confirmApprove}
          onCancel={() => {
            setShowApproveModal(false);
            setActionSupplierId(null);
          }}
          isLoading={actionLoading}
        />

        <ConfirmActionModal
          isOpen={showRejectModal}
          title="Rechazar Proveedor"
          message="¿Estás seguro de que deseas rechazar este proveedor? Esta acción cambiará su estado a 'Rechazado'."
          confirmText="Rechazar"
          variante="peligro"
          onConfirm={confirmReject}
          onCancel={() => {
            setShowRejectModal(false);
            setActionSupplierId(null);
          }}
          isLoading={actionLoading}
        />

        <ConfirmActionModal
          isOpen={showDeleteModal}
          title="Eliminar Solicitud"
          message="¿Estás seguro de eliminar esta solicitud? Esta acción es destructiva y no se puede deshacer."
          confirmText="Eliminar"
          variante="peligro"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setActionSupplierId(null);
          }}
          isLoading={actionLoading}
        />

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

export default SolicitudesProveedores;
